import { InjectionToken, NgModule } from '@angular/core';
import { EcommerceMappersModule } from '@epicuro-next/addons/ecommerce-tracking-mappers';
import { ProductDataModule } from '@epicuro-next/data/product';
import { ConfigurationModule } from '@epicuro-next/platform/configuration';
import {
  BROWSER_STORAGE_SERVICE,
  BROWSER_STORAGE_TOKEN,
} from '@epicuro-next/platform/storage';
import { BasketStateModule } from '@epicuro-next/state/basket-state';
import { DeprecatedMerchandisePricesModule } from '@epicuro-next/state/deprecated-merchandise-prices';
import {
  resetStateMetaReducer,
  storageMetaReducer,
} from '@epicuro-next/state/meta-reducers';
import { PBasketStateModule } from '@epicuro-next/state/p-basket-state';
import { ProductEntitiesModule } from '@epicuro-next/state/product-entities';
import { ProductListStoreModule } from '@epicuro-next/state/product-list-state';
import { ShopTheLookStateModule } from '@epicuro-next/state/shop-the-look-state';
import { SnackbarStateModule } from '@epicuro-next/state/snackbar';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';

import { FEATURE_NAME } from './configurations';
import {
  ConfigStripEffects,
  ConfigStripPrefilledEffects,
  ConfigStripScaleSetEffects,
  ConfigStripValidationsEffects,
  DeliveryPeriodsEffects,
} from './state/effects';
import { ConfigStripActivationEffects } from './state/effects/activation.effects';
import { ConfigStripBackgroundProductsEffects } from './state/effects/background-products.effects';
import { ConfigStripPricesEffects } from './state/effects/config-strip-prices.effects';
import { ProductEntitiesFromConfigStripEffects } from './state/effects/config-strip-product-entities.effects';
import { ProductSizeRunsEffects } from './state/effects/config-strip-size-runs.effects';
import { ConfigStripDeactivationEffects } from './state/effects/deactivation.effects';
import { ConfigStripEcommerceTrackingEffects } from './state/effects/ecommerce-tracking.effects';
import { PreviewSizesCacheEffects } from './state/effects/preview-sizes-cache.effects';
import { ConfigStripSourceEffects } from './state/effects/source.effects';
import { businessStateKeys } from './state/reducers/config-strip-business.reducer';
import { initialState, reducer } from './state/reducers/config-strip.reducer';
import { CONFIGSTRIP_FEATURE_KEY } from './state/state-config';

const CONFIGSTRIP_STORAGE_KEY = new InjectionToken(
  'CONFIGSTRIP_STORAGE_KEY Injection Token',
);
const CONFIGSTRIP_STORAGE_KEYS = new InjectionToken(
  'CONFIGSTRIP_STORAGE_KEYS Injection Token',
);
const CONFIG_STRIP_TOKEN = new InjectionToken(
  'CONFIG_STRIP_TOKEN Injection Token',
);

export function getConfigStripFactoryConfig(
  saveKeys: string[],
  browserStorageKey: string,
  storageService: BROWSER_STORAGE_SERVICE,
) {
  return {
    metaReducers: [
      storageMetaReducer(saveKeys, browserStorageKey, storageService),
      resetStateMetaReducer(initialState),
    ],
  };
}

@NgModule({
  imports: [
    ProductDataModule,
    StoreModule.forFeature(
      CONFIGSTRIP_FEATURE_KEY,
      reducer,
      CONFIG_STRIP_TOKEN,
    ),
    EffectsModule.forFeature([
      ConfigStripEffects,
      ConfigStripScaleSetEffects,
      PreviewSizesCacheEffects,
      ConfigStripPrefilledEffects,
      ConfigStripValidationsEffects,
      DeliveryPeriodsEffects,
      ProductEntitiesFromConfigStripEffects,
      ConfigStripPricesEffects,
      ConfigStripEcommerceTrackingEffects,
      ProductSizeRunsEffects,
      ConfigStripActivationEffects,
      ConfigStripDeactivationEffects,
      ConfigStripBackgroundProductsEffects,
      ConfigStripSourceEffects,
    ]),
    ConfigurationModule.forFeature(FEATURE_NAME),
    BasketStateModule,
    SnackbarStateModule,
    PBasketStateModule,
    ProductListStoreModule,
    DeprecatedMerchandisePricesModule,
    ProductEntitiesModule,
    EcommerceMappersModule,
    ShopTheLookStateModule,
  ],
  providers: [
    {
      provide: CONFIGSTRIP_STORAGE_KEY,
      useValue: 'config_strip_storage',
    },
    {
      provide: CONFIGSTRIP_STORAGE_KEYS,
      useValue: businessStateKeys.map((key) => `business.${key}`),
    },
    {
      provide: CONFIG_STRIP_TOKEN,
      deps: [
        CONFIGSTRIP_STORAGE_KEYS,
        CONFIGSTRIP_STORAGE_KEY,
        BROWSER_STORAGE_TOKEN,
      ],
      useFactory: getConfigStripFactoryConfig,
    },
  ],
})
export class ConfigStripStateModule {}
